import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

const MonitorTooltip = ({ collectors }: { collectors: string[] }) => {
  const { t } = useTranslation(['asset', 'assetNetwork', 'common',
    'applicationsSecurity',
    'applicationsAssetManagement',
    'assetField',
    'applicationsAnalyzerManagement'
  ]);
  return (
    <>
      <div>
        {t('This monitor uses data collected by the following collector(s)')}
      </div>
      <ul className="list-disc mt-2 ml-8 mb-2 mr-0">
        {collectors.map(collector => (
          <li key={collector}>{collector}</li>
        ))}
      </ul>
    </>
  );
};

const APPLICATIONS_SECURITY_NS = { ns: 'applicationsSecurity' as const };
const APPLICATIONS_ASSETMANAGEMENT_NS = { ns: 'applicationsAssetManagement' as const };
const ASSETFIELD_NS = {ns:'assetField' as const};
const APPLICATIONS_ANALYZER_MANAGEMENT_NS = {ns:'applicationsAnalyzerManagement' as const};

export function getMonitorDescriptionByName(
  monitorName: string,
  t: TFunction
): { name: string; tooltip: React.ReactNode | null } {
  if (monitorName.endsWith('HeartbeatAverageUptimeOneHour')) {
    return {
      name: t('Heartbeat average last hour'),
      tooltip: (
        <MonitorTooltip collectors={[t('Operational Status - Heartbeat')]} />
      ),
    };
  }

  if (monitorName.endsWith('HeartbeatAverageUptimeSixHours')) {
    return {
      name: t('Heartbeat average last 6 hours'),
      tooltip: (
        <MonitorTooltip collectors={[t('Operational Status - Heartbeat')]} />
      ),
    };
  }

  if (monitorName.endsWith('HeartbeatAverageUptimeTwentyFourHours')) {
    return {
      name: t('Heartbeat average last 24 hours'),
      tooltip: (
        <MonitorTooltip collectors={[t('Operational Status - Heartbeat')]} />
      ),
    };
  }

  switch (monitorName) {
    case 'ComputeAverageCPUTwentyFourHours':
    case 'ComputeWindowsAgentlessAverageCPUTwentyFourHours':
      return {
        name: t('CPU average last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'NetworkAverageCPUTwentyFourHours':
      return {
        name: t('CPU average last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU & Memory')]} />,
      };
    case 'ComputeAverageCPUSevenDays':
    case 'ComputeWindowsAgentlessAverageCPUSevenDays':
      return {
        name: t('CPU average last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'NetworkAverageCPUSevenDays':
      return {
        name: t('CPU average last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU & Memory')]} />,
      };
    case 'ComputeAverageCPUTwoHours':
    case 'ComputeWindowsAgentlessAverageCPUTwoHours':
      return {
        name: t('CPU average last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'NetworkAverageCPUTwoHours':
      return {
        name: t('CPU average last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU & Memory')]} />,
      };
    case 'BCVAverageCPUTwoHours':
    case 'FCSAverageCPUTwoHours':
    case 'SCSAverageCPUTwoHours':
      return {
        name: t('CPU average last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('CPU')]} />,
      };
    case 'BCVAverageCPUSevenDays':
    case 'FCSAverageCPUSevenDays':
    case 'SCSAverageCPUSevenDays':
      return {
        name: t('CPU average last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('CPU')]} />,
      };
    case 'BCVAverageCPUTwentyFourHours':
    case 'FCSAverageCPUTwentyFourHours':
    case 'SCSAverageCPUTwentyFourHours':
      return {
        name: t('CPU average last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('CPU')]} />,
      };
    case 'ComputeDiskLogicalFreeBytes':
    case 'ComputeDiskFreeBytes':
      return {
        name: t('Disk size available in bytes'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Volumes')]} />,
      };
    case 'ComputeDiskLogicalTotalFreeBytes':
      return {
        name: t('Total disk size available in bytes'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Volumes')]} />,
      };
    case 'ComputeDiskLogicalTotalUsedPCT':
    case 'ComputeDiskTotalUsedPCT':
      return {
        name: t('Total disk usage in %'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Volumes')]} />,
      };
    case 'ComputeDiskLogicalUsedPCT':
    case 'ComputeDiskUsedPCT':
      return {
        name: t('Disk usage in %'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Volumes')]} />,
      };
    case 'ComputeDiskStatus':
      return {
        name: t('Disk status'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Disks')]} />,
      };
    case 'ComputeEventCriticalCountOneHour':
      return {
        name: t('Critical events last hour'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'ComputeEventCriticalCountTwentyFourHours':
      return {
        name: t('Critical events last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'ComputeMemoryAveragePercentageTwentyFourHours':
      return {
        name: t('Memory average last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'NetworkMemoryAveragePercentageTwentyFourHours':
      return {
        name: t('Memory average last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU & Memory')]} />,
      };
    case 'ComputeMemoryAveragePercentageTwoHours':
      return {
        name: t('Memory average last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'NetworkMemoryAveragePercentageTwoHours':
      return {
        name: t('Memory average last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU & Memory')]} />,
      };
    case 'ComputeMemoryAveragePercentageSevenDays':
      return {
        name: t('Memory average last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'NetworkMemoryAveragePercentageSevenDays':
      return {
        name: t('Memory average last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU & Memory')]} />,
      };
    case 'VeritasSSREventCountOneDay':
      return {
        name: t('Veritas System Recovery events last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSREventCountSevenDays':
      return {
        name: t('Veritas System Recovery events last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSREventCountOneMonth':
      return {
        name: t('Veritas System Recovery events last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSRFailedBackupsOneDay':
      return {
        name: t('Veritas System Recovery failed backups last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSRFailedBackupsSevenDays':
      return {
        name: t('Veritas System Recovery failed backups last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSRFailedBackupsOneMonth':
      return {
        name: t('Veritas System Recovery failed backups last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSRFailedRecoveriesOneDay':
      return {
        name: t('Veritas System Recovery failed recoveries last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSRFailedRecoveriesSevenDays':
      return {
        name: t('Veritas System Recovery failed recoveries last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasSSRFailedRecoveriesOneMonth':
      return {
        name: t('Veritas System Recovery failed recoveries last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasBackupEventCountOneDay':
      return {
        name: t('Veritas BackupExec events last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasBackupEventCountSevenDays':
      return {
        name: t('Veritas BackupExec events last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasBackupEventCountOneMonth':
      return {
        name: t('Veritas BackupExec events last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasBackupFailedJobsOneDay':
      return {
        name: t('Veritas BackupExec failed jobs last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasBackupFailedJobsSevenDays':
      return {
        name: t('Veritas BackupExec failed jobs last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VeritasBackupFailedJobsOneMonth':
      return {
        name: t('Veritas BackupExec failed jobs last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs')]} />,
      };
    case 'VolumeProtectionStatus':
      return {
        name: t('Volume protection status'),
        tooltip: <MonitorTooltip collectors={[t('Inventory')]} />,
      };
    case 'TPMActivationStatus':
      return {
        name: t('TPM activation status'),
        tooltip: <MonitorTooltip collectors={[t('Inventory')]} />,
      };
    case 'TPMEnableStatus':
      return {
        name: t('TPM enable status'),
        tooltip: <MonitorTooltip collectors={[t('Inventory')]} />,
      };
    case 'PRMParametersActualValue':
      return { name: t('PRM Parameters Actual Value'), tooltip: null };
    case 'PRMEventsOverviewTwoHours':
      return { name: t('Events Overview 2 hours'), tooltip: null };
    case 'ISAEControllabilityStatus':
      return {
        name: t('Last Controllability status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Controllability')]} />
        ),
      };
    case 'ISAEFailurePredictionStatus':
      return {
        name: t('Last Failure prediction status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Failure Prediction')]} />
        ),
      };
    case 'ISAEHuntingStatus':
      return {
        name: t('Last Hunting status'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Hunting')]} />,
      };
    case 'ISAEInadequateAirStatus':
      return {
        name: t('Last Inadequate air status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Inadequate Air')]} />
        ),
      };
    case 'ISAELinkageStatus':
      return {
        name: t('Last Linkage status'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Linkage')]} />,
      };
    case 'ISAEStictionStatus':
      return {
        name: t('Last Stiction status'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Stiction')]} />,
      };
    case 'ISAEParameterMonitoringStatus':
      return {
        name: t('Last Parameter Monitoring status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Parameter Monitoring')]} />
        ),
      };
    case 'BCVAverageVNETLoadTwentyFourHours':
    case 'FCSAverageVNETLoadTwentyFourHours':
    case 'SCSAverageVNETLoadTwentyFourHours':
    case 'AVRAverageVNETLoadTwentyFourHours':
      return {
        name: t('Average VNET load last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('VNET')]} />,
      };
    case 'BCVAverageVNETLoadTwoHours':
    case 'FCSAverageVNETLoadTwoHours':
    case 'SCSAverageVNETLoadTwoHours':
    case 'AVRAverageVNETLoadTwoHours':
      return {
        name: t('Average VNET load last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('VNET')]} />,
      };
    case 'BCVAverageVNETLoadSevenDays':
    case 'FCSAverageVNETLoadSevenDays':
    case 'SCSAverageVNETLoadSevenDays':
    case 'AVRAverageVNETLoadSevenDays':
      return {
        name: t('Average VNET load last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('VNET')]} />,
      };
    case 'BCVAverageTempAirInTwentyFourHours':
    case 'FCSAverageTempAirInTwentyFourHours':
    case 'SCSAverageTempAirInTwentyFourHours':
      return {
        name: t('Average air in temperature last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempAirInTwoHours':
    case 'FCSAverageTempAirInTwoHours':
    case 'SCSAverageTempAirInTwoHours':
      return {
        name: t('Average air in temperature last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempAirInSevenDays':
    case 'FCSAverageTempAirInSevenDays':
    case 'SCSAverageTempAirInSevenDays':
      return {
        name: t('Average air in temperature last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempAirOutTwentyFourHours':
    case 'FCSAverageTempAirOutTwentyFourHours':
    case 'SCSAverageTempAirOutTwentyFourHours':
      return {
        name: t('Average air out temperature last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempAirOutTwoHours':
    case 'FCSAverageTempAirOutTwoHours':
    case 'SCSAverageTempAirOutTwoHours':
      return {
        name: t('Average air out temperature last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempAirOutSevenDays':
    case 'FCSAverageTempAirOutSevenDays':
    case 'SCSAverageTempAirOutSevenDays':
      return {
        name: t('Average air out temperature last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempBatteryTwentyFourHours':
    case 'FCSAverageTempBatteryTwentyFourHours':
    case 'SCSAverageTempBatteryTwentyFourHours':
      return {
        name: t('Average battery temperature last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempBatteryTwoHours':
    case 'FCSAverageTempBatteryTwoHours':
    case 'SCSAverageTempBatteryTwoHours':
      return {
        name: t('Average battery temperature last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageTempBatterySevenDays':
    case 'FCSAverageTempBatterySevenDays':
    case 'SCSAverageTempBatterySevenDays':
      return {
        name: t('Average battery temperature last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('HKU')]} />,
      };
    case 'BCVAverageECCErrorCount':
    case 'FCSAverageECCErrorCount':
    case 'SCSAverageECCErrorCount':
      return {
        name: t('Average ECC Error count'),
        tooltip: <MonitorTooltip collectors={[t('ECC')]} />,
      };
    case 'BCVLatestCPUCardStatus':
    case 'FCSLatestCPUCardStatus':
    case 'SCSLatestCPUCardStatus':
      return {
        name: t('Latest CPU Card Status'),
        tooltip: <MonitorTooltip collectors={[t('Revision Info')]} />,
      };
    case 'BCVLatestPSUCardStatus':
    case 'FCSLatestPSUCardStatus':
    case 'SCSLatestPSUCardStatus':
      return {
        name: t('Latest PSU Card Status'),
        tooltip: <MonitorTooltip collectors={[t('Revision Info')]} />,
      };
    case 'FCNLatestCPUStatus':
      return {
        name: t('Latest CPU Status'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU Status')]} />,
      };
    case 'FCNErrorLogCountTwentyFourHours':
      return {
        name: t('Error log count last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Stardom Logs')]} />,
      };
    case 'FCNErrorLogCountSevenDays':
      return {
        name: t('Error log count last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Stardom Logs')]} />,
      };
    case 'FCNCriticalLogCountTwentyFourHours':
      return {
        name: t('Critical log count last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Stardom Logs')]} />,
      };
    case 'FCNCriticalLogCountSevenDays':
      return {
        name: t('Critical log count last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Stardom Logs')]} />,
      };
    case 'NetworkInterfaceAdminState':
      return {
        name: t('Interface admin state'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Interfaces')]} />,
      };
    case 'NetworkInterfaceOperationalState':
      return {
        name: t('Interface operational state'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Interfaces')]} />,
      };
    case 'NetworkInterfaceSpeed':
      return {
        name: t('Interface speed'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Interfaces')]} />,
      };
    case 'NetworkVlanAdminState':
      return {
        name: t('Vlan admin state'),
        tooltip: <MonitorTooltip collectors={[t("Inventory - VLAN's")]} />,
      };
    case 'NetworkAverageTemperatureTwentyFourHours':
      return {
        name: t('Average temperature last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Temperature')]} />,
      };
    case 'NetworkAverageTemperatureTwoHours':
      return {
        name: t('Average temperature last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Temperature')]} />,
      };
    case 'EPOClientThreatsOneHour':
      return {
        name: t('Client threats', APPLICATIONS_SECURITY_NS),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Client Threats')]} />,
      };
    case 'EPOClientManagementStatus':
      return {
        name: t('Client management status', APPLICATIONS_SECURITY_NS),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Trellix Agents')]} />,
      };
    case 'EPOClientContentVersion':
      return {
        name: t('Client content version', APPLICATIONS_SECURITY_NS),
        tooltip: <MonitorTooltip collectors={[t('Inventory - Trellix Agents')]} />,
      };
    case 'EPOClientProductVersion':
      return {
        name: t('Client product version', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip collectors={[t('Inventory - Installed Products')]} />
        ),
      };
    case 'EPOSolidcoreClientStatus':
      return {
        name: t('Solidcore latest client status', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Inventory')]} />
        ),
      };
    case 'EPOClientOnAccessScanStatus':
      return {
        name: t('ESTP - On access scan status', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'EPOClientAccessProtectionStatus':
      return {
        name: t('ESTP - Access protection status', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'EPOSolidcoreServiceStatus':
      return {
        name: t('Solidcore latest service status', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Solidcore Metrics')]} />
        ),
      };
    case 'EPOClientEventsOneHour':
      return {
        name: t('Client events', APPLICATIONS_SECURITY_NS),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Client Events')]} />,
      };
    case 'EPOServerAuditLogs':
      return {
        name: t('Server audit logs', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Server Audit Logs')]} />
        ),
      };
    case 'EPOSolidcoreHighSeverityEvents':
      return {
        name: t(
          'Solidcore high severity events count last 24 hours',
          APPLICATIONS_SECURITY_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Solidcore Events')]} />,
      };
    case 'EPOESTPExploitPreventionStatus':
      return {
        name: t('ESTP - Exploit prevention status', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'EPOESTPAccessProtectionCompliance':
      return {
        name: t('ESTP - Access protection compliancy', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'EPOESTPExploitPreventionCompliance':
      return {
        name: t('ESTP - Exploit prevention compliancy', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'EPOESTPOnAccessScanCompliance':
      return {
        name: t('ESTP - On access scan compliancy', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'EPOESTPOnDemandScanCompliance':
      return {
        name: t('ESTP - On demand scan compliancy', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'EPOESTPAVContentManagerCompliance':
      return {
        name: t('ESTP - AV Content manager compliancy', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t('Metrics - Product Endpoint Security Threat Prevention'),
            ]}
          />
        ),
      };
    case 'WSUSServerSyncStatus':
      return {
        name: t('Server synchronization state', APPLICATIONS_SECURITY_NS),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Events')]} />,
      };
    case 'WSUSServerSyncStatusTwentyFourHours':
      return {
        name: t(
          'Server synchronization state last 24 hours',
          APPLICATIONS_SECURITY_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Events')]} />,
      };
    case 'WSUSServerSyncStatusSevenDays':
      return {
        name: t(
          'Server synchronization state last 7 days',
          APPLICATIONS_SECURITY_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Events')]} />,
      };
    case 'WSUSServerSyncStatusThirtyDays':
      return {
        name: t('Server synchronization state last 30 days', APPLICATIONS_SECURITY_NS),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Events')]} />,
      };
    case 'WSUSServerEventCountLast24Hours':
      return {
        name: t('Server events last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Events')]} />,
      };
    case 'WSUSClientSyncStatus':
      return {
        name: t('Client synchronization state', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip
            collectors={[t('Metrics - Missing patches per computer')]}
          />
        ),
      };
    case 'WSUSNonInstalledApprovedUpdates':
      return {
        name: t('Client non installed approved updates', APPLICATIONS_SECURITY_NS),
        tooltip: (
          <MonitorTooltip collectors={[t('Inventory - Applicable Patches')]} />
        ),
      };
    case 'WSUSNonInstalledApprovedCriticalUpdates':
      return {
        name: t(
          'Client non installed critical approved updates',
          APPLICATIONS_SECURITY_NS
        ),
        tooltip: (
          <MonitorTooltip collectors={[t('Inventory - Applicable Patches')]} />
        ),
      };
    case 'EnvironmentalAssetAverageTemperatureTwoHours':
      return {
        name: t('Average temperature last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageTemperatureTwentyFourHours':
      return {
        name: t('Average temperature last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageTemperatureSevenDays':
      return {
        name: t('Average temperature last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'NetworkAverageTemperatureSevenDays':
      return {
        name: t('Average temperature last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Temperature')]} />,
      };
    case 'EnvironmentalAssetAverageHumidityTwoHours':
      return {
        name: t('Average humidity last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageHumidityTwentyFourHours':
      return {
        name: t('Average humidity last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageHumiditySevenDays':
      return {
        name: t('Average humidity last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageIsolationTwoHours':
      return {
        name: t('Average isolation last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageIsolationTwentyFourHours':
      return {
        name: t('Average isolation last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageIsolationSevenDays':
      return {
        name: t('Average isolation last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageContactTwoHours':
      return {
        name: t('Average contact last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageContactTwentyFourHours':
      return {
        name: t('Average contact last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageContactSevenDays':
      return {
        name: t('Average contact last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageDustTwoHours':
      return {
        name: t('Average dust last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageDustTwentyFourHours':
      return {
        name: t('Average dust last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageDustSevenDays':
      return {
        name: t('Average dust last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageCorrosionTwoHours':
      return {
        name: t('Average corrosion last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageCorrosionTwentyFourHours':
      return {
        name: t('Average corrosion last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'EnvironmentalAssetAverageCorrosionSevenDays':
      return {
        name: t('Average corrosion last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('ODU Collectors - Sensor')]} />,
      };
    case 'PRMAuditLogsDeviceDeletedTwoHours':
      return {
        name: t('Device Deleted'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Audit Logs')]} />,
      };
    case 'PRMAuthLogsUserLogout':
      return {
        name: t('User logout'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Audit Logs')]} />,
      };
    case 'PRMAuthLogsUserLogin':
      return {
        name: t('User login'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Authentication Logs')]} />
        ),
      };
    case 'PRMAuditLogsParameterSaveScheduler':
      return {
        name: t('Parameter Save Scheduler finished', APPLICATIONS_ASSETMANAGEMENT_NS),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Audit Logs')]} />,
      };
    case 'PRMCriticalPASEvents':
      return {
        name: t('Critical PAS events'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - PAS Events')]} />,
      };
    case 'AAIMSAnalyserAvailabilityRateSevenDays':
      return {
        name: t(
          'Average availability rate last 7 days', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserAvailabilityRateThirtyDays':
      return {
        name: t(
          'Average availability rate last 30 days', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserAvailabilityRateTwentyFourHours':
      return {
        name: t(
          'Average availability rate last 24 hours', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserBreakdownRateSevenDays':
      return {
        name: t(
          'Average breakdown rate last 7 days', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserBreakdownRateThirtyDays':
      return {
        name: t(
          'Average breakdown rate last 30 days', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserBreakdownRateTwentyFourHours':
      return {
        name: t(
          'Average breakdown rate last 24 hours', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserCheckingRateSevenDays':
      return {
        name: t(
          'Average checking rate last 7 days', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserCheckingRateThirtyDays':
      return {
        name: t(
          'Average checking rate last 30 days', APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'AAIMSAnalyserCheckingRateTwentyFourHours':
      return {
        name: t(
          'Average checking rate last 24 hours',APPLICATIONS_ANALYZER_MANAGEMENT_NS
        ),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'FieldParameterValues':
      return { name: t('Field Parameters'), tooltip: null };
    case 'FieldNamurState':
      return {
        name: t('Namur State'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Operational Status - Asset Status (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'FieldHighPriorityMaintenanceEvents':
      return {
        name: t('Count of high priority maintenance events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Maintenance Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'FieldMediumPriorityMaintenanceEvents':
      return {
        name: t('Count of medium priority maintenance events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Maintenance Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'FieldLowPriorityMaintenanceEvents':
      return {
        name: t('Count of low priority maintenance events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Maintenance Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'FieldHighPriorityDiagnosticEvents':
      return {
        name: t('Count of high priority diagnostic events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Diagnostic Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'FieldMediumPriorityDiagnosticEvents':
      return {
        name: t('Count of medium priority diagnostic events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Diagnostic Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'FieldLowPriorityDiagnosticEvents':
      return {
        name: t('Count of low priority diagnostic events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Diagnostic Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'AllUnacknowledgedHighPriorityMaintenanceEvents':
      return {
        name: t(
          'All unacknowledged high priority maintenance events', ASSETFIELD_NS
        ),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Diagnostic Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'AllUnacknowledgedMediumPriorityMaintenanceEvents':
      return {
        name: t(
          'All unacknowledged medium priority maintenance events', ASSETFIELD_NS
        ),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Diagnostic Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'AllUnacknowledgedLowPriorityMaintenanceEvents':
      return {
        name: t(
          'All unacknowledged low priority maintenance events', ASSETFIELD_NS
        ),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Diagnostic Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'AllUnacknowledgedMaintenanceEvents':
      return {
        name: t('All unacknowledged maintenance events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Diagnostic Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'AllUnacknowledgedCMD48Events':
      return {
        name: t('All unacknowledged CMD48 events'),
        tooltip: (
          <MonitorTooltip
            collectors={[
              t(
                'Event Logs - Advanced Maintenance Events (Asset Management Applications - PRM)'
              ),
            ]}
          />
        ),
      };
    case 'ApplicationCentumAllHistoricalMessages':
      return {
        name: t('All historical messages'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldBusACFError':
      return {
        name: t('Fieldbus ACF error'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldBusACPAlarm':
      return {
        name: t('Fieldbus ACP alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldbusChannelSlaveDeviceAlarm':
      return {
        name: t('Fieldbus channel slave device alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldbusDeviceProcessAlarm':
      return {
        name: t('Fieldbus device process alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationApplicationCentumFieldbusDeviceAlarm':
      return {
        name: t('Fieldbus device alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldbusFieldDeviceAlarm':
      return {
        name: t('Fieldbus field device alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldbusHARTAlarm':
      return {
        name: t('Fieldbus HART alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldbusModuleSlaveDeviceAlarm':
      return {
        name: t('Fieldbus module slave device alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldbusSlaveDeviceCommonAlarm':
      return {
        name: t('Fieldbus slave device common alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumFieldbusSlaveDeviceSpecificAlarm':
      return {
        name: t('Fieldbus device specific alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumProcessAnnunciatorAlarm':
      return {
        name: t('Process annunciator alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumProcessAnnunciatorReAlarm':
      return {
        name: t('Process annunciator re-alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumProcessBlockAlarm':
      return {
        name: t('Process block alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumProcessBlockReAlarm':
      return {
        name: t('Process block re-alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumSystemApplicationsAlarm':
      return {
        name: t('System applications alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumSystemFCSAlarm':
      return {
        name: t('System FCS alarm'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumSystemMaintenanceError':
      return {
        name: t('System maintenance error'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumForcingEvents':
      return {
        name: t('Forcing Events'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumIOPEvents':
      return {
        name: t('IOP Events'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'ApplicationCentumMOSEvents':
      return {
        name: t('MOS Events'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Historical Messages')]} />
        ),
      };
    case 'VeeamFailedBackupSessionsTwentyFourHours':
      return {
        name: t('Veeam Failed Backup Sessions last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Backup Sessions')]} />,
      };
    case 'VeeamFailedBackupSessionsSevenDays':
      return {
        name: t('Veeam Failed Backup Sessions last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Backup Sessions')]} />,
      };
    case 'VeeamFailedBackupSessionsThirtyDays':
      return {
        name: t('Veeam Failed Backup Sessions last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Backup Sessions')]} />,
      };
    case 'VeeamFailedReplicaSessionsTwentyFourHours':
      return {
        name: t('Veeam Failed Replica Sessions last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Replica Sessions')]} />,
      };
    case 'VeeamFailedReplicaSessionsSevenDays':
      return {
        name: t('Veeam Failed Replica Sessions last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Replica Sessions')]} />,
      };
    case 'VeeamFailedReplicaSessionsThirtyDays':
      return {
        name: t('Veeam Failed Replica Sessions last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Replica Sessions')]} />,
      };
    case 'VeeamFailedRestoreSessionsTwentyFourHours':
      return {
        name: t('Veeam Failed Restore Sessions last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Restore Sessions')]} />,
      };
    case 'VeeamFailedRestoreSessionsSevenDays':
      return {
        name: t('Veeam Failed Restore Sessions last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Restore Sessions')]} />,
      };
    case 'VeeamFailedRestoreSessionsThirtyDays':
      return {
        name: t('Veeam Failed Restore Sessions last 30 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Restore Sessions')]} />,
      };
    case 'ClarotyAlertsOneHour':
      return {
        name: t('Claroty Alerts last hour'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'ClarotyAlertsTwentyFourHours':
      return {
        name: t('Claroty Alerts last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'ClarotyAlertsSevenDays':
      return {
        name: t('Claroty Alerts last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'ClarotyCriticalAlertsOneHour':
      return {
        name: t('Claroty Critical Alerts last hour'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'ClarotyCriticalAlertsTwentyFourHours':
      return {
        name: t('Claroty Critical Alerts last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'ClarotyCriticalAlertsSevenDays':
      return {
        name: t('Claroty Critical Alerts last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'NozomiAlertsOneHour':
      return {
        name: t('Nozomi Alerts last hour'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'NozomiAlertsTwentyFourHours':
      return {
        name: t('Nozomi Alerts last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'NozomiAlertsSevenDays':
      return {
        name: t('Nozomi Alerts last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'NozomiCriticalAlertsOneHour':
      return {
        name: t('Nozomi Critical Alerts last hour'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'NozomiVulnerabilitiesTwentyFourHours':
      return {
        name: t('All vulnerabilities in last 24 hours '),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Vulnerabilities')]} />,
      };
    case 'NozomiVulnerabilitiesSevenDays':
      return {
        name: t('All vulnerabilities in last 7 days '),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Vulnerabilities')]} />,
      };
    case 'NozomiCPEChangesSevenDays':
      return {
        name: t('All CPE changes in last 7 days '),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - CPE Changes')]} />,
      };
    case 'NozomiCriticalAlertsTwentyFourHours':
      return {
        name: t('Nozomi Critical Alerts last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'NozomiCriticalAlertsSevenDays':
      return {
        name: t('Nozomi Critical Alerts last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Event Logs - Alerts')]} />,
      };
    case 'WhatsupGoldLatestDeviceStatus':
      return {
        name: t('WhatsUp Gold Latest Device Status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Inventory - Device Overview')]} />
        ),
      };
    case 'WhatsupGoldLatestScanStatus':
      return {
        name: t('WhatsUp Gold Latest Scan Status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Inventory - Device Scans')]} />
        ),
      };
    case 'MicrosoftHyperVEventLogs':
      return {
        name: t('Microsoft Hyper-V Event Logs'),
        tooltip: <MonitorTooltip collectors={[t('Logs')]} />,
      };
    case 'MicrosoftHyperVSwitchHealthStatus':
      return {
        name: t('Microsoft Hyper-V Switch Health Status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Inventory - Virtual Switches')]} />
        ),
      };
    case 'MicrosoftHyperVSwitchStatus':
      return {
        name: t('Microsoft Hyper-V Switch Status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Inventory - Virtual Switches')]} />
        ),
      };
    case 'MicrosoftHyperVVMAverageCPUTwoHours':
      return {
        name: t('Microsoft Hyper-V VM Average CPU last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'MicrosoftHyperVVMAverageCPUTwentyFourHours':
      return {
        name: t('Microsoft Hyper-V VM Average CPU last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'MicrosoftHyperVVMAverageCPUSevenDays':
      return {
        name: t('Microsoft Hyper-V VM Average CPU last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'MicrosoftHyperVVMAverageMemoryAssignedTwoHours':
      return {
        name: t('Microsoft Hyper-V VM Average Memory Assigned last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'MicrosoftHyperVVMAverageMemoryAssignedTwentyFourHours':
      return {
        name: t('Microsoft Hyper-V VM Average Memory Assigned last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'MicrosoftHyperVVMAverageMemoryAssignedSevenDays':
      return {
        name: t('Microsoft Hyper-V VM Average Memory Assigned last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'MicrosoftHyperVVMAverageMemoryDemandTwoHours':
      return {
        name: t('Microsoft Hyper-V VM Average Memory Demand last 2 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'MicrosoftHyperVVMAverageMemoryDemandTwentyFourHours':
      return {
        name: t('Microsoft Hyper-V VM Average Memory Demand last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'MicrosoftHyperVVMAverageMemoryDemandSevenDays':
      return {
        name: t('Microsoft Hyper-V VM Average Memory Demand last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'MicrosoftHyperVVMEnabledStatus':
      return {
        name: t('Microsoft Hyper-V VM Enabled Status'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - VM Overview')]} />,
      };
    case 'MicrosoftHyperVVMHealthStatus':
      return {
        name: t('Microsoft Hyper-V VM Health Status'),
        tooltip: <MonitorTooltip collectors={[t('Inventory - VM Overview')]} />,
      };
    case 'CalsysUtilityRateLastDay':
      return {
        name: t('Calsys Utility Rate last day'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysUtilityRateLastSevenDays':
      return {
        name: t('Calsys Utility Rate last seven days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysBreakdownRateLastDay':
      return {
        name: t('Calsys Breakdown Rate last day'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysBreakdownRateLastSevenDays':
      return {
        name: t('Calsys Breakdown Rate last seven days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysFaultRateLastDay':
      return {
        name: t('Calsys Fault Rate last day'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysFaultRateLastSevenDays':
      return {
        name: t('Calsys Fault Rate last seven days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysRoutineMaintenanceRateLastDay':
      return {
        name: t('Calsys Routine Maintenance Rate last day'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysRoutineMaintenanceRateLastSevenDays':
      return {
        name: t('Calsys Routine Maintenance Rate last seven days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysNonRoutineMaintenanceRateLastDay':
      return {
        name: t('Calsys Non Routine Maintenance Rate last days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysNonRoutineMaintenanceRateLastSevenDays':
      return {
        name: t('Calsys Non Routine Maintenance Rate last seven days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Performance')]} />,
      };
    case 'CalsysBadValidationResultsLastSevenDays':
      return {
        name: t('Calsys Bad Validation Results last seven days'),
        tooltip: (
          <MonitorTooltip collectors={[t('Event Logs - Validation Events')]} />
        ),
      };
    case 'AVRLatestVNETBusStatus':
    case 'BCVLatestVNETBusStatus':
    case 'FCSLatestVNETBusStatus':
    case 'HMISTNLatestVNETBusStatus':
    case 'SCSLatestVNETBusStatus':
    case 'WACLatestVNETBusStatus':
      return {
        name: t('Latest VNET Bus Status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Collectors - VNET Bus Status')]} />
        ),
      };
    case 'FieldRangeComparison':
      return {
        name: t('HART range comparison'),
        tooltip: (
          <MonitorTooltip collectors={[
              t('Metrics - Parameters (Asset Management Applications - PRM)'),
              t('Metrics - OPC Range levels (Asset Management Applications - PRM)')
          ]} />
        ),
      };
    case 'SiemensS7AverageCPUTwentyFourHours':
      return {
        name: t('CPU average last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'SiemensS7AverageCPUSevenDays':
      return {
        name: t('CPU average last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - CPU')]} />,
      };
    case 'SiemensS7AverageMemoryDataTwentyFourHours':
      return {
        name: t('Memory data average last 24 hours'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'SiemensS7AverageMemoryDataSevenDays':
      return {
        name: t('Memory data average last 7 days'),
        tooltip: <MonitorTooltip collectors={[t('Metrics - Memory')]} />,
      };
    case 'SiemensS7PSUStatus':
      return {
        name: t('PSU maintenance status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Maintenance Status')]} />
        ),
      };
    case 'SiemensS7RackStatus':
      return {
        name: t('Rack maintenance status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Maintenance Status')]} />
        ),
      };
    case 'SiemensS7SlotStatus':
      return {
        name: t('Slot maintenance status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Maintenance Status')]} />
        ),
      };
    case 'SiemensGenericMaintenanceStatus':
      return {
        name: t('Maintenance status'),
        tooltip: (
          <MonitorTooltip collectors={[t('Metrics - Maintenance Status')]} />
        ),
      };
    default:
      return { name: t('Unknown'), tooltip: null };
  }
}
